import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowLeft,
  CalendarDays,
  CarFront,
  CircleCheckBig,
  CircleDollarSign,
  Clock3,
  FlagTriangleRight,
  Footprints,
  Mail,
  MapPin,
  TriangleAlert,
  UsersRound,
} from "lucide-react"
import { useState } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { PublicPagePending } from "@/components/loading-skeleton"
import { PublicHeader } from "@/components/public-header"
import { StatusChip } from "@/components/status-chip"
import { Button } from "@/components/ui/button"
import {
  formatCourseDate,
  formatCurrency,
  formatMinutes,
  formatTimestamp,
} from "@/lib/date"
import { golfDeskQueryKeys, myBookingQueryOptions } from "@/lib/query-options"
import {
  abandonBookingCheckoutFn,
  cancelMyBookingFn,
  syncBookingCheckoutFn,
} from "@/server/functions"

type BookingDetailSearch = {
  checkout?: "success" | "cancelled"
}

export const Route = createFileRoute("/my-bookings/$bookingId")({
  validateSearch: (search: Record<string, unknown>): BookingDetailSearch => ({
    checkout:
      search.checkout === "success" || search.checkout === "cancelled"
        ? search.checkout
        : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, params, deps }) => {
    if (deps.checkout === "success") {
      await syncBookingCheckoutFn({ data: { bookingId: params.bookingId } })
    } else if (deps.checkout === "cancelled") {
      await abandonBookingCheckoutFn({ data: { bookingId: params.bookingId } })
    }

    const options = myBookingQueryOptions(params.bookingId)
    await context.queryClient.invalidateQueries({ queryKey: options.queryKey })
    return context.queryClient.fetchQuery(options)
  },
  head: () => ({
    meta: [{ title: "Booking details · Groundline" }],
  }),
  pendingComponent: PublicPagePending,
  component: BookingDetails,
})

function BookingDetails() {
  const { bookingId } = Route.useParams()
  const search = Route.useSearch()
  const { data } = useSuspenseQuery(myBookingQueryOptions(bookingId))
  const queryClient = useQueryClient()
  const [cancelling, setCancelling] = useState(false)
  const [error, setError] = useState("")

  if (!data.booking) {
    return (
      <>
        <PublicHeader viewer={data.viewer} />
        <DemoNotice />
        <main className="site-frame grid min-h-[70vh] place-items-center py-16 text-center">
          <div>
            <FlagTriangleRight className="mx-auto size-7 text-muted-foreground" />
            <h1 className="page-title">Booking not found.</h1>
            <p className="page-lede mx-auto">
              {data.viewer
                ? "It may belong to another demo account or no longer exist."
                : "Check the full guest confirmation link, or choose another tee time."}
            </p>
            {data.viewer ? (
              <Button
                render={<Link to="/my-bookings" />}
                variant="outline"
                className="mt-6"
              >
                View my bookings
              </Button>
            ) : (
              <Button
                render={<Link to="/" />}
                variant="accent"
                className="mt-6"
              >
                Back to availability
              </Button>
            )}
          </div>
        </main>
      </>
    )
  }

  const booking = data.booking
  const isGuestBooking = booking.isGuest
  const isPaid = booking.paymentStatus === "paid"
  const isPending = booking.status === "pending_payment"
  const checkoutCancelled =
    search.checkout === "cancelled" || booking.paymentStatus === "failed"

  async function cancelBooking() {
    if (
      !window.confirm(
        `Cancel ${booking.reference}? This only changes synthetic demo data.`
      )
    ) {
      return
    }

    setCancelling(true)
    setError("")
    try {
      const result = await cancelMyBookingFn({
        data: { bookingId: booking.id },
      })
      if (!result.ok) {
        setError("Only a confirmed upcoming booking can be cancelled.")
      }
      await queryClient.invalidateQueries({
        queryKey: golfDeskQueryKeys.all,
      })
    } catch {
      setError("The booking could not be cancelled.")
    } finally {
      setCancelling(false)
    }
  }

  const TransportIcon =
    booking.cartPreference === "riding" ? CarFront : Footprints

  return (
    <>
      <PublicHeader viewer={data.viewer} />
      <DemoNotice />
      <main className="site-frame py-10 sm:py-14">
        <Button
          render={<Link to={isGuestBooking ? "/" : "/my-bookings"} />}
          variant="ghost"
          className="-ml-3"
        >
          <ArrowLeft data-icon="inline-start" />
          {isGuestBooking ? "Book another round" : "My bookings"}
        </Button>

        <div className="mt-5 grid gap-7 lg:grid-cols-[minmax(0,1fr)_350px] lg:items-start">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="eyebrow">Booking {booking.reference}</span>
              <StatusChip status={booking.status} />
            </div>
            <h1 className="page-title">
              {checkoutCancelled
                ? "Checkout wasn’t completed."
                : isPending
                  ? "Your tee time is being held."
                  : "Your group is on the rail."}
            </h1>
            <p className="page-lede">
              {checkoutCancelled
                ? "The temporary hold has been released and no payment was taken. Choose another time whenever you’re ready."
                : isPending
                  ? "Stripe is still confirming the test payment. Refresh this page in a moment; the signed webhook will settle the booking."
                  : isGuestBooking
                    ? "Your guest booking is confirmed without an account. Staff can find it by name, email, or reference."
                    : "Staff can see this group on their live dispatch board. Any status change made there will appear here too."}
            </p>

            {search.checkout === "success" && isPaid ? (
              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-[#9fbeb8] bg-[#dcebe7] p-4 text-sm">
                <CircleCheckBig className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <strong className="font-semibold">
                    Stripe test payment confirmed
                  </strong>
                  <p className="mt-1 leading-6">
                    The booking and payment ledger were updated from the
                    Checkout Session.
                  </p>
                </div>
              </div>
            ) : checkoutCancelled ? (
              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-[#d1aa73] bg-[#f4ead8] p-4 text-sm">
                <TriangleAlert className="mt-0.5 size-5 shrink-0 text-[#8a5523]" />
                <div>
                  <strong className="font-semibold">No charge was made</strong>
                  <p className="mt-1 leading-6">
                    Cancelling checkout released the held spots immediately.
                  </p>
                </div>
              </div>
            ) : isPending ? (
              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-[#9fbeb8] bg-[#dcebe7] p-4 text-sm">
                <Clock3 className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <strong className="font-semibold">Payment pending</strong>
                  <p className="mt-1 leading-6">
                    The booking will confirm as soon as Stripe reports payment.
                  </p>
                </div>
              </div>
            ) : isGuestBooking ? (
              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-[#9fbeb8] bg-[#dcebe7] p-4 text-sm">
                <Mail className="mt-0.5 size-5 shrink-0 text-primary" />
                <div>
                  <strong className="font-semibold">
                    Guest booking confirmed
                  </strong>
                  <p className="mt-1 leading-6">
                    No account was created. Save this page and reference{" "}
                    <span className="font-semibold">{booking.reference}</span>.
                  </p>
                </div>
              </div>
            ) : null}

            <section className="ruled-panel mt-8 overflow-hidden">
              <div className="ruled-panel__head">
                <div>
                  <span className="eyebrow">
                    <UsersRound className="size-3.5" />
                    Player manifest
                  </span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {booking.playerCount} players · lead is used for course
                    contact
                  </p>
                </div>
              </div>
              {data.players.map((player) => (
                <div
                  key={player.id}
                  className="rule-row grid gap-3 px-5 py-4 sm:grid-cols-[1fr_auto] sm:items-center"
                >
                  <div className="flex items-center gap-3">
                    <span className="grid size-9 place-items-center rounded-full bg-primary font-display text-sm font-semibold text-white">
                      {player.position}
                    </span>
                    <span>
                      <strong className="block text-sm">{player.name}</strong>
                      <small className="mt-0.5 block text-xs text-muted-foreground capitalize">
                        {player.kind}
                      </small>
                    </span>
                  </div>
                  {player.email ? (
                    <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail className="size-3 shrink-0" />
                      <span className="break-all">{player.email}</span>
                    </span>
                  ) : null}
                </div>
              ))}
            </section>

            <section className="ruled-panel mt-5 overflow-hidden">
              <div className="ruled-panel__head">
                <div>
                  <span className="eyebrow">Booking record</span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Created {formatTimestamp(booking.createdAt)}
                  </p>
                </div>
              </div>
              <dl className="grid sm:grid-cols-2">
                {[
                  [
                    "Payment",
                    booking.paymentStatus === "paid"
                      ? "Paid with Stripe"
                      : booking.paymentStatus.replaceAll("_", " "),
                  ],
                  [
                    "Booked as",
                    isGuestBooking
                      ? "Guest checkout"
                      : booking.source.replace("_", " "),
                  ],
                  [
                    "Transport",
                    booking.cartPreference === "riding"
                      ? `${booking.cartCount} shared ${
                          booking.cartCount === 1 ? "cart" : "carts"
                        }`
                      : "Walking",
                  ],
                  ["Course note", booking.notes || "None"],
                ].map(([label, value], index) => (
                  <div
                    key={label}
                    className={`p-5 ${
                      index < 2 ? "border-b border-border" : ""
                    } ${index % 2 === 0 ? "sm:border-r sm:border-border" : ""}`}
                  >
                    <dt className="text-[0.65rem] font-bold tracking-[0.1em] text-muted-foreground uppercase">
                      {label}
                    </dt>
                    <dd className="mt-2 text-sm font-semibold capitalize">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>

            {error ? (
              <p className="action-error mt-5" role="alert">
                {error}
              </p>
            ) : null}

            {data.viewer &&
            !isGuestBooking &&
            booking.status === "confirmed" ? (
              <Button
                variant="destructive"
                className="mt-6"
                disabled={cancelling}
                onClick={cancelBooking}
              >
                {cancelling ? "Cancelling…" : "Cancel demo booking"}
              </Button>
            ) : null}
          </div>

          <aside className="ruled-panel overflow-hidden lg:sticky lg:top-[94px]">
            <div className="bg-primary p-5 text-white">
              <span className="text-[0.65rem] font-bold tracking-[0.13em] text-[#b8ccc6] uppercase">
                Starter tag
              </span>
              <strong className="mt-2 block font-display text-[2.35rem] leading-none font-semibold uppercase">
                {formatMinutes(booking.teeTime.startMinutes)}
              </strong>
              <span className="mt-2 block text-xs text-[#c4d4cf]">
                {formatCourseDate(booking.teeTime.date)}
              </span>
            </div>
            <div className="grid gap-4 p-5 text-sm">
              <SummaryRow
                icon={MapPin}
                title={booking.course.name}
                copy={booking.course.location}
              />
              <SummaryRow
                icon={CalendarDays}
                title={`${booking.teeTime.holes} holes`}
                copy={`${booking.playerCount} player group`}
              />
              <SummaryRow
                icon={TransportIcon}
                title={
                  booking.cartPreference === "riding" ? "Riding" : "Walking"
                }
                copy="Transport preference"
              />
              <div className="mt-1 flex items-end justify-between gap-3 border-t border-dashed border-border pt-4">
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <CircleDollarSign className="size-4 text-primary" />
                  {isPaid ? "Paid total" : "Booking total"}
                </span>
                <strong className="font-display text-[2rem] leading-none font-semibold">
                  {formatCurrency(booking.totalCents, booking.course.currency)}
                </strong>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </>
  )
}

function SummaryRow({
  icon: Icon,
  title,
  copy,
}: {
  icon: typeof MapPin
  title: string
  copy: string
}) {
  return (
    <div className="flex gap-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <span>
        <strong className="block">{title}</strong>
        <small className="mt-1 block text-muted-foreground">{copy}</small>
      </span>
    </div>
  )
}
